import { useFollows } from 'hooks/useFollows'
import { useMutation } from 'hooks/useMutation'
import { useQueryClient } from 'hooks/useQueryClient'

import { deleteFollow, fetchFollow, postFollow } from 'utils/queries/follows'
import { createPartialUser } from 'utils/user/createPartialUser'

import { Follow } from 'types/follow'
import { Data } from 'types/query'
import { PartialUser } from 'types/user'

import { useCurrentUser } from './useCurrentUser'
import { useQuery } from './useQuery'

export const useFollow = ({ trainer }: { trainer: PartialUser | undefined }) => {
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const trainerId = trainer?.id

  const { data: trainerFollows } = useFollows(trainerId)
  const trainerFollowsCurrentUser = trainerFollows?.find(
    (userFollow) => userFollow.trainer.id === currentUser?.id,
  )

  const followQuery = useQuery<Follow | null>(['follows', trainerId, currentUser?.id], () =>
    fetchFollow({ trainerId, userId: currentUser?.id }),
  )

  const follow = useMutation(
    async () => {
      if (!currentUser || !trainer) throw new Error('Error following user')
      const result = await postFollow({
        follower: createPartialUser(currentUser),
        trainer: createPartialUser(trainer),
      })
      return result
    },
    {
      onSuccess: () => {
        queryClient.setQueryData(['user', trainerId, ''], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.followerCount = data.followerCount ? data.followerCount + 1 : 1
          }
          return data
        })

        queryClient.setQueryData(['user', '', trainer?.username], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.followerCount = data.followerCount ? data.followerCount + 1 : 1
          }
          return data
        })

        if (trainerFollowsCurrentUser) {
          queryClient.setQueryData(['user', trainerId, ''], (data: Data) => {
            if (data) {
              // eslint-disable-next-line no-param-reassign
              data.friendCount = data.friendCount ? data.friendCount + 1 : 1
            }
            return data
          })

          queryClient.setQueryData(['user', '', trainer?.username], (data: Data) => {
            if (data) {
              // eslint-disable-next-line no-param-reassign
              data.friendCount = data.friendCount ? data.friendCount + 1 : 1
            }
            return data
          })

          queryClient.setQueryData(['friends', currentUser?.id], (data: Data) => {
            if (data) {
              data.push(trainer)
            }
            return data
          })
        }

        queryClient.invalidateQueries(['follows', trainerId])
        queryClient.invalidateQueries(['follows', currentUser?.id])
        queryClient.invalidateQueries('events')
      },
    },
  )

  const unfollow = useMutation((data: any) => deleteFollow(data), {
    onSuccess: () => {
      queryClient.setQueryData(['user', trainerId, ''], (data: Data) => {
        if (data) {
          // eslint-disable-next-line no-param-reassign
          data.followerCount = data.followerCount ? data.followerCount - 1 : 1
        }
        return data
      })

      queryClient.setQueryData(['user', '', trainer?.username], (data: Data) => {
        if (data) {
          // eslint-disable-next-line no-param-reassign
          data.followerCount = data.followerCount ? data.followerCount - 1 : 1
        }
        return data
      })

      queryClient.setQueryData(['follows', trainerId, currentUser?.id], () => {
        return null
      })

      if (trainerFollowsCurrentUser) {
        queryClient.setQueryData(['user', trainerId, ''], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.friendCount = data.friendCount ? data.friendCount - 1 : 1
          }
          return data
        })

        queryClient.setQueryData(['user', '', trainer?.username], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.friendCount = data.friendCount ? data.friendCount - 1 : 1
          }
          return data
        })

        queryClient.setQueryData(['friends', currentUser?.id], (data: Data) => {
          if (data) {
            const trainerIndex = data.findIndex((friend) => friend.id === trainerId)
            if (trainerIndex > -1) {
              data.splice(trainerIndex, 1)
            }
          }
          return data
        })
      }

      queryClient.invalidateQueries(['follows', trainerId])
      queryClient.invalidateQueries(['follows', currentUser?.id])
      queryClient.invalidateQueries('events')
    },
  })
  return {
    ...followQuery,
    follow,
    unfollow,
  }
}
